import React, { Component } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { reduxForm, SubmissionError, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import mixpanel from 'mixpanel-browser';

import { Button, Input, Select, Datepicker } from 'common/components';
import {
  ANALYTICS,
  CROP_SIZE,
  ERROR_DEFAULT,
  OPTIONS,
  UPDATE_MAP_FIELDS,
} from 'common/constants';
import { graphQlToFormErrors, getLatestDiary, omitDeep } from 'common/utils';
import {
  maxValue,
  maxLength,
  minValue,
  required,
  numberOnly,
  noPercent,
} from 'common/form-validators';
import * as normalizers from 'common/normalizers';
import {
  MUTATION_CREATE_CROP,
  MUTATION_UPDATE_CROP,
  FETCH_CROP,
} from 'api/crop';
import { FETCH_FIELD_DETAILS_WITH_NAME } from 'api/field';
import { generateFieldId } from 'util/ids';
import { toCommaNumber, shortenFormat } from 'common/formatters';

export class CropForm extends Component {
  constructor(props) {
    super(props);

    // validations
    this.farmSizeMin = value => minValue(value, CROP_SIZE.MIN);
    this.varietyMax = value => maxLength(value, CROP_SIZE.MAX_TITLE);

    // normlizers
    this.normalizeFarmSize = value =>
      normalizers.limitDigits(normalizers.onlyNumbers(value), 13, 2);
    this.shortenFormat = value =>
      shortenFormat(value, CROP_SIZE.MAX_TITLE_SHORTEN);
  }

  // cannot be in constructor, because initialValues change after graphQL query
  plantedAreaMax = value => {
    // if this is edit mode, do max + initial value
    const max =
      this.props.plantedArea +
      (this.props.id === 'add' ? 0 : this.props.initialValues.plantedArea);
    return (
      maxValue(value, max) &&
      `Only ${max} Acres available on this field, try again`
    );
  };

  cropNameTaken = value => {
    const { crops, selectedCrop } = this.props;
    let match =
      crops &&
      crops.filter(
        crop => crop.crop.name === selectedCrop && crop.crop.variety === value
      );
    return match && match.length > 0
      ? `"${selectedCrop} ${value}" already exists`
      : undefined;
  };

  render() {
    const {
      onSubmit,
      closeForm,
      error,
      handleSubmit,
      valid,
      pristine,
      submitting,
      id,
      selectedSeeding,
      selectedHarvest,
      season,
    } = this.props;
    const isEditMode = id !== 'add';
    const disableHarvest = selectedSeeding
      ? [{ before: new Date(selectedSeeding) }]
      : [];
    const disableSeeding = selectedHarvest
      ? [{ after: new Date(selectedHarvest) }]
      : [];

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <small className={'text--info'}>* Required</small>
        <h2 className={'text--header'}>
          {isEditMode ? 'Edit Crop' : 'Add Crop'}
        </h2>

        <Select
          name="crop"
          disabled={submitting || isEditMode}
          title="Crop"
          options={OPTIONS.CROP_OPTIONS}
          validate={[required]}
        />

        <Input
          name="variety"
          disabled={submitting || isEditMode}
          title="Variety"
          validate={[this.varietyMax, noPercent, this.cropNameTaken]}
          format={isEditMode ? this.shortenFormat : null}
          normalizeOnBlur={normalizers.trim}
        />

        <Datepicker
          name="seedDate"
          disabled={submitting}
          title="Seed Date"
          season={season}
          disabledDays={disableSeeding}
        />

        <Datepicker
          name="harvestDate"
          disabled={submitting}
          season={season}
          title="Expected Harvest Date"
          disabledDays={disableHarvest}
        />

        <Input
          name="plantedArea"
          disabled={submitting}
          title="Planted Area"
          infoText="Acres"
          validate={[
            required,
            numberOnly,
            this.farmSizeMin,
            this.plantedAreaMax,
          ]}
          normalize={this.normalizeFarmSize}
          format={toCommaNumber}
        />

        <Input
          name="expectedYield"
          disabled={submitting}
          title="Expected Yield"
          infoText="Bushels/Acre"
          validate={[required, numberOnly]}
          normalize={this.normalizeFarmSize}
          format={toCommaNumber}
        />

        {error && <p className="text--error">{error}</p>}

        <div className={'field__group'}>
          <fieldset className={'field text--center'}>
            <Button
              type="submit"
              className="btn btn--primary gap-top-small"
              disabled={!valid || pristine || submitting}
            >
              Save
            </Button>
          </fieldset>

          <fieldset className={'field text--center'}>
            <Button
              type="cancel"
              className="btn"
              onClick={closeForm}
              disabled={submitting}
            >
              Cancel
            </Button>
          </fieldset>
        </div>
      </form>
    );
  }
}

CropForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  closeForm: PropTypes.func.isRequired,
  error: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,

  // manually
  id: PropTypes.string,
  plantedArea: PropTypes.number,
  crops: PropTypes.array,

  // values from mapstate
  selectedCrop: PropTypes.string,
  selectedSeeding: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  selectedHarvest: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  initialValues: PropTypes.object,
  season: PropTypes.number.isRequired,
};

CropForm.defaultProps = {
  selectedSeeding: '',
  season: new Date().getFullYear(),
};

const formSettings = { form: 'cropform', enableReinitialize: true };
const field = formValueSelector(formSettings.form);
const mapState = (state, { plantedArea, initialValues }) => {
  initialValues = initialValues || { plantedArea };

  return {
    season: state.settings.season,
    selectedCrop: field(state, 'crop'),
    selectedSeeding: field(state, 'seedDate'),
    selectedHarvest: field(state, 'harvestDate'),
    initialValues,
  };
};

const mapDispatch = (dispatch, props) => ({
  onSubmit: async values => {
    let vars = {
      harvestDate: values.harvestDate,
      seedDate: values.seedDate,
      plantedArea: {
        size: values.plantedArea,
        type: 'acres', // BE has default values too
      },
    };

    // if yield updated or this is add mode (do not submit empty object)
    const initialYield =
      props.initialValues && props.initialValues.expectedYield;
    if (
      (props.id === 'add' && initialYield) ||
      initialYield !== values.expectedYield
    ) {
      vars = {
        ...vars,
        expectedYield: values.expectedYield
          ? {
              size: values.expectedYield,
              type: 'bushels', // BE has default values too
              per: 'acres', // BE has default values too
            }
          : null, // null can be saved after BE update
      };
    }
    // add crops data only for add mode
    if (props.id === 'add') {
      vars = {
        ...vars,
        fieldName: props.fieldName,
        season: props.season,
        crop: {
          name: values.crop,
          variety: values.variety && values.variety.trim(),
        },
      };
    }

    const res =
      props.id === 'add'
        ? await props.onCreateCrop(vars)
        : await props.onUpdateCrop(props.id, vars);

    if (
      res &&
      res.data &&
      (res.data.createCropForField || res.data.updateCropForField)
    ) {
      // update map fields
      const fields = props.mapFields.map(field => {
        if (field && field.name === props.fieldName) {
          // push the new crop to the target field
          if (props.id === 'add' && res.data.createCropForField) {
            field.crops.push(res.data.createCropForField);
          }
          // update the existing crop in the target field
          if (props.id !== 'add' && res.data.updateCropForField) {
            field.crops = field.crops.map(crop => {
              if (crop.id === res.data.updateCropForField.id) {
                return res.data.updateCropForField;
              }
              return crop;
            });
          }
          return field;
        }
        return field;
      });

      dispatch({ type: UPDATE_MAP_FIELDS, payload: fields });
      props.closeForm();
    } else if (res && res.errors) {
      const _error = res.errors[0] ? res.errors[0].message : ERROR_DEFAULT;
      throw new SubmissionError({ _error });
    } else {
      throw new SubmissionError({ _error: ERROR_DEFAULT });
    }
  },
});

const buildCropchemaForOptimisticResponse = payload => {
  const { crop, expectedYield, plantedArea } = payload;
  payload.__typename = 'FieldCrop';
  if (crop) {
    crop.__typename = 'CropIdentifier';
  }

  if (expectedYield && Array.isArray(expectedYield)) {
    for (const index in expectedYield) {
      expectedYield[index].__typename = 'YieldDiary';
      expectedYield[index].yield.harvested = false;
      expectedYield[index].yield.__typename = 'Yield';
    }
  }

  if (plantedArea) {
    plantedArea.__typename = 'Area';
  }

  return payload;
};

export default compose(
  graphql(FETCH_CROP, {
    options: props => ({
      variables: { id: props.id },
      fetchPolicy: 'cache-and-network',
    }),
    props: props => {
      if (!props.data || !props.data.getCropForField) {
        return;
      }

      const crop = props.data.getCropForField;
      const expectedYield = getLatestDiary(crop.expectedYield);

      return {
        initialValues: {
          crop: crop.crop && crop.crop.name,
          variety: crop.crop && crop.crop.variety,
          expectedYield:
            expectedYield && expectedYield.yield && expectedYield.yield.size
              ? expectedYield.yield.size
              : '',
          harvestDate: crop.harvestDate,
          seedDate: crop.seedDate,
          plantedArea: crop.plantedArea
            ? crop.plantedArea.size
            : props.plantedArea,
        },
        ...props,
      };
    },
  }),

  graphql(MUTATION_CREATE_CROP, {
    options: () => ({
      onCompleted: ({ createCropForField }) => {
        if (createCropForField) {
          const {
            crop,
            plantedArea,
            season,
            expectedYield,
          } = createCropForField;
          mixpanel.track(ANALYTICS.EVENTS.CROP.ADDED, {
            [ANALYTICS.PROPERTIES.CROP.NAME]: crop.name,
            [ANALYTICS.PROPERTIES.CROP.VARIETY]: crop.variety || '',
            [ANALYTICS.PROPERTIES.CROP.PLANTED_AREA]: plantedArea.size,
            [ANALYTICS.PROPERTIES.CROP.SEASON]: season,
            [ANALYTICS.PROPERTIES.CROP.EXPECTED_YIELD]:
              expectedYield[0].yield.size,
          });

          mixpanel.people.increment(ANALYTICS.PROPERTIES.ACCOUNT.CROPS_ADDED);
        }

        return createCropForField;
      },
    }),
    props: props => ({
      onCreateCrop: fieldCropCreateInput => {
        const { season, fieldName, crop } = fieldCropCreateInput;
        const expectedId = `CROPFIELD:${season}:${fieldName}:${
          crop.name
        }:${crop.variety || ''}`;

        const constructedCrop = {
          id: expectedId,
          expectedPrice: null,
          modifiedAt: null,
          createdAt: new Date().toISOString(),
          harvestDate: null,
          seedDate: null,
          ...fieldCropCreateInput,
          crop: {
            name: fieldCropCreateInput.crop.name,
            variety: fieldCropCreateInput.crop.variety || null,
          },
          expectedYield: [
            {
              timestamp: new Date().toISOString(),
              yield: fieldCropCreateInput.expectedYield,
            },
          ],
        };

        // stringify date object to avoid omitting
        const omittedInput = omitDeep(
          {
            ...fieldCropCreateInput,
            harvestDate: fieldCropCreateInput.harvestDate
              ? fieldCropCreateInput.harvestDate.toISOString()
              : null,
            seedDate: fieldCropCreateInput.seedDate
              ? fieldCropCreateInput.seedDate.toISOString()
              : null,
          },
          '__typename'
        );

        return props
          .mutate({
            variables: {
              fieldCropCreateInput: omittedInput,
            },
            optimisticResponse: () => ({
              createCropForField: buildCropchemaForOptimisticResponse(
                constructedCrop
              ),
            }),
            // this is called twice when in online mode
            update: (cache, { data: { createCropForField } }) => {
              const { season, fieldName } = fieldCropCreateInput;
              const cachedData = cache.readQuery({
                query: FETCH_FIELD_DETAILS_WITH_NAME,
                variables: {
                  season,
                  active: true,
                  name: fieldName,
                  id: generateFieldId(fieldName),
                },
              });

              cachedData.getAllCropsForField = [
                ...cachedData.getAllCropsForField.filter(
                  crop => crop.id !== createCropForField.id
                ),
                buildCropchemaForOptimisticResponse(createCropForField),
              ];

              const cachedObj = {
                query: FETCH_FIELD_DETAILS_WITH_NAME,
                variables: {
                  season,
                  active: true,
                  name: fieldName,
                  id: generateFieldId(fieldName),
                },
                data: cachedData,
              };
              cache.writeQuery(cachedObj);
            },
          })
          .then(res => res)
          .catch(graphQlToFormErrors);
      },
      ...props,
    }),
  }),

  graphql(MUTATION_UPDATE_CROP, {
    props: props => ({
      onUpdateCrop: (id, fieldCropUpdateInput) => {
        return props
          .mutate({
            variables: { id, fieldCropUpdateInput },
          })
          .then(res => res)
          .catch(graphQlToFormErrors);
      },
      ...props,
    }),
  }),

  connect(
    mapState,
    mapDispatch
  ),
  reduxForm(formSettings)
)(CropForm);



// WEBPACK FOOTER //
// ./src/containers/CropForm/index.js